import { Currency } from '../utils/currency'
import { IUser } from './RegisterTypes'

export interface IAccountForm {
  phoneNumber: string
  firstName: string
  lastName: string
  email: string
  emailConsent?: boolean
  notificationsConsent?: boolean
  billingAddress: string
  billingCompanyName: string
  billingCompanyVatNumber: string
  birthDate?: string
  vehicleRegistrationNumbers?: string
  nationalInsuranceNumber?: string
  driverLicenseNumber?: string
}

export interface IPaymentForm {
  driver: IAccountForm
  partner: {
    code: string
  }
  voucherCode: string
}

export interface IDescription {
  reference: string;
  title: string;
  subTitle: string;
  imageUrls: string[] | null;
  header: string;
  subHeader: string;
  body: string;

}

export interface IConfirmedBookingResponse extends IBooking {
  booking: IConfirmedBooking;
  confirmationBackgroundImageUrl: string;
  paymentIntentRequireAction?: boolean;
  paymentIntentClientSecret?: string;
}

export interface IConfirmedBooking extends IBooking {
  reference: string;
}

export interface IBooking {
  product: IProductLight;
  location: ILocationLight;
  vehicleModel: IVehicleModelLight;
  price: IPrice;
  insurances?: IInsurances;
  options?: IOptions;
  alternative?: IAlternative;
  bookingAdvantagesFooter: string;
  bookingAdvantages: string
  privacyPolicyUrl: string | null
  termsAndConditionUrl: string | null
  schedule: ISchedule;
  requirements: IRequirements;
  payment?: {
    payment?: string;
    creditCardReference?: string;
    postPaidCompanyReference?: string;
    method?: string;
    intent?: string;
    purchaseOrder?: string
  };
}

export enum DriverInsuranceConnectorFields {
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  EMAIL = 'email',
  BIRTH_DATE = 'birth_date',
  PHONE = 'phone_number',
  BILLING_ADDRESS = 'billing_address',
  DRIVER_LICENSE_NUMBER = 'driver_license_number',
  NATIONAL_INSURANCE_NUMBER = 'national_insurance_number',
  VEHICLE_REGISTRATION_NUMBERS = 'vehicle_registration_numbers',
  PRIVATE_HIRE_DRIVER_LICENSE_NUMBER = 'private_hire_driver_licence_number',
  PRIVATE_HIRE_DRIVER_LICENSE_ISSUE_AT = 'private_hire_driver_licence_issued_at'
}

export interface IRequirements {
  minimumAge?: number;
  maximumAge?: number;
  mandatoryFields: DriverInsuranceConnectorFields[]
}

export interface IAlternative {
  reference: string;
  label?: string;
  level?: 'alert' | 'warning' | 'info' | 'success';
  isScheduleChanged: boolean;
  schedule?: ISchedule;
  isLocationChanged: boolean;
  location?: ILocationLight;
  isVehicleModelChanged: boolean;
  vehicleModel?: IVehicleModelLight;
}

export interface IVehicleModelLight {
  reference: string;
  descriptionReference: string;
  manufacturer: string;
  name: string;
  imageUrl: string;
  range: number;
  energyType: 'FUEL' | 'ELECTRIC'
}

export interface IFullVehicleModel extends IVehicleModelLight {
  descriptionImageUrls: string[] | null;
  descriptionHeader: string | null;
  descriptionSubHeader: string | null;
  descriptionBody: string | null;
}

export interface IPrice extends IPriceOption {
  pricingReference?: string;
  voucherCode?: string;
}

export interface IDescriptionLink {
  label: string;
  reference?: string;
}

export interface IScreen {
  header?: IScreenHeader;
  footer?: IScreenFooter;
}

export interface IScreenHeader {
  title: string;
  propositions: IDescriptionLink[];
}

export interface IScreenFooter {
  template?: string
  advices: IDescriptionLink[];
}

export interface IProposedBookings {
  bookings: IBooking[];
  screen?: IScreen;
  inError?: boolean;
  bookingMessage?: string
  privatePolicyUrl?: string | null
  showWaitingList?: boolean;
}

export interface IPriceOption {
  currency: Currency;
  currencySymbol?: Currency;
  amount: number;
  amountPerUnit?: number;
  discountsAmount?: number
}

export interface IInsurances {
  value?: string;
  price?: IPriceOption;
  titles?: string[];
  insurances?: IInsurance[];
  screen?: IScreen;
  excess?: IPrice;
  descriptionReference?: string

}

export interface IInsurance {
  imageUrl?: string;
  description?: string;
  value?: string;
  selected?: true;
  descriptionReference?: string;
  originPriceAmount?: number;
  price?: IPriceOption;
  labels: {
    title: string;
    header: string;
  };
}

export enum DeliveryValues {
  BAY = 'bay',
  DELIVERY_ADDRESS = 'delivery_address'
}

export interface IDeliveries {
  deliveries: IDelivery[];
  type: DeliveryValues;
  value: string; // address
  latitude: number | null;
  longitude: number | null;
  price: IPrice;
  screen: IScreen;
  descriptionReference?: string
}

export interface IDelivery {
  imageUrl: string;
  description: string;
  type: DeliveryValues;
  value: string;
  latitude: number | null;
  longitude: number | null;
  selected: boolean;
  available: boolean;
  previouslySelected?: boolean;
  descriptionReference?: string;
  originPriceAmount: number;
  price: IPriceOption;
  labels?: {
    title: string;
    header: string;
  };
}

export type ITerm = {
  reference: string
  label: string
  url?: string | null
  document?: unknown | null
}

export type IOneWayLocation = {
  reference: string
  name: string
  address: string
  latitude: number
  longitude: number
  imageUrl: string | null
  iconUrl: string | null
}

export type IOneWay = {
  value: string // reference
  price: IPriceOption
  locations: IOneWayLocation[]
}

export interface IOptions {
  descriptions?: Array<string>;
  insurances?: IInsurances;
  mileage?: IMileage;
  energy?: IEnergy
  loyaltyCashback?: ILoyalty
  arkPoints?: ICO2
  deliveries?: IDeliveries
  terms?: Array<ITerm>
  oneWay?: IOneWay
}

export interface IOption {
  labels: {
    title: string;
    header: string;
  };
  price?: IPriceOption;
  descriptionReference?: string
}

export interface IEnergy extends IOption {
  savings: { amount: number, unit: string }
}

export interface ILoyalty extends IOption {
  cashback: { amount: number, unit: string }
}

export interface ICO2 extends IOption {
  points: number
  trees: number
}

export interface IMileage extends IOption {
  unit: string;
  minRange: number;
  maxRange: number;
  stepRange: number;
  value: number;
  titles: string[];
  descriptionReference: string;
  screen?: IScreen;
}

export enum CarStatus {
  alternative = 'alternative',
  available = 'available',
  notAvailable = 'notAvailable'
}

export interface ICountryLight {
  code: string
}
export interface ICityLight {
  reference: string;
  priority: number;
  imageUrl: string;
  timezone: string;
  name: string;
  available: true;
  message: string;
  latitude: number;
  longitude: number;
  country: ICountryLight
}

export enum ProductType {
  STANDARD = 'standard',
  HIRE_PURCHASE = 'hire_purchase',
  APPOINTMENT = 'appointment',
}

export enum PaymentMode {
  CASH_PAYMENT = 'cash_payment',
  SUBSCRIPTION = 'subscription',
  NONE = 'none',
}

export interface IProductLight {
  description: string;
  durationUnit: number;
  marketingLabel: string;
  name: string;
  reference: string;
  scheduleUnit: number;
  paymentMode: PaymentMode;
  customPriceLabel: string;
  type: ProductType;
  amountRoundingPrecision: number;
}

export interface ILocationLight {
  reference: string;
  priority: number;
  name: string;
  imageUrl: string;
  iconUrl: string;
  color: string;
  timezone: string;
  available: true;
  message: string;
  latitude: number;
  longitude: number;
  address: string;
  rangePolygone: string | null
  city: ICityLight;
}

export interface ICompany {
  reference: string;
  name: string;
}

export interface IPaymentOptions {
  paymentPublicApi?: string;
  paymentAccountId?: string;
  userCreditCards: ICreditCard[];
  company?: {
    reference: string;
    name: string;
  };
  loyaltyProgram?: ILoyaltyProgram;
  postPaidCompany?: ICompany;
}

export interface ILoyaltyProgram {
  usedAmount?: number;
  isBalanceAvailable?: boolean;
  message?: string;
}

export interface ICreditCard {
  reference: string;
  priority?: number;
  default: boolean;
  brand: string;
  country: string;
  expMonth: number;
  expYear: number;
  last4: string;
  brandName?: string;
  imageUrl: string;
  paymentMethod: string;
}

export interface ISchedule {
  startAt: string;
  endAt: string;
  duration: number;
  timezone: string;
}

export type JwtUserTokenPayload = {
  data: Data
  exp: string
}

type Data = {
  requestId: string
  scope: 'tnc' | 'ops'
  user: IUser
}
